<template>
  <div
    class="progress-stage-loader"
    :style="{
      color: forceTextColor || undefined,
      fontFamily: forceFontFamily || undefined
    }"
  >
    <div class="progress-header">
      <h1 class="title">
        {{ displayTitle }}
      </h1>
      <p class="description">
        {{ displayDescription }}
        You will also receive an email update once your results are ready
      </p>
    </div>

    <div class="progress-container">
      <div class="stages-list">
        <div
          v-for="(stage, index) in stageConfigs"
          :key="stage.id"
          class="stage-item"
          :class="{
            'completed': completedStages.includes(stage.id),
            'active': currentStage === stage.id,
            'pending': !completedStages.includes(stage.id) && currentStage !== stage.id
          }"
        >
          <div class="stage-indicator">
            <div
              v-if="completedStages.includes(stage.id)"
              class="check-icon"
            >
              ✓
            </div>
            <div
              v-else-if="currentStage === stage.id"
              class="loading-spinner"
            />
            <div
              v-else
              class="stage-number"
            >
              {{ index + 1 }}
            </div>
          </div>
          <div class="stage-info">
            <span class="stage-name">{{ stage.title }}</span>
            <span
              v-if="currentStage === stage.id"
              class="stage-status"
            >Processing...</span>
          </div>
        </div>
      </div>

      <div class="progress-bar-container">
        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: `${overallProgress}%` }"
          />
        </div>
        <div class="progress-text">
          {{ Math.round(overallProgress) }}% Complete
        </div>
      </div>
    </div>

    <div
      v-if="error"
      class="error-message"
    >
      <div class="error-title">
        <img
          :src="errorIconSrc"
          class="error-icon"
          alt="error-icon"
        >
        <div
          class="error-text"
          :style="{ color: forceTextColor || undefined }"
        >
          <strong>Loading is taking longer than usual.</strong>
        </div>
      </div>
      <p :style="{ color: forceTextColor || undefined }">{{ error }}</p>
      <div v-if="showReportBtn" class="report-issue">
        <span>Something is not right? <br></span>
        <a @click="$emit('report-issue-click')">Report an issue</a>
      </div>
    </div>

    <div
      v-if="!error"
      class="status-message"
    >
      <p>{{ statusMessage || 'Please wait while we process your data...' }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import errorIcon from '../../assets/loader-error.svg';

export type CalculationStage = 'scrape' | 'versionUpdate' | 'featurama' | 'otis';

export interface StageConfig {
  id: CalculationStage;
  title: string;
  description: string;
  estimatedDuration: number;
}

export interface FlowConfig {
  title: string;
  description: string;
  subDescription?: string;
  stageConfigs?: StageConfig[];
  stageMessages?: Record<CalculationStage, string>;
}

interface Props {
  currentStage: CalculationStage | null;
  completedStages: CalculationStage[];
  error: string | null;
  statusMessage?: string;
  flowConfig?: FlowConfig;
  forceTextColor?: string;
  forceFontFamily?: string;
  errorIconSrc?: string;
  showReportBtn?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  statusMessage: '',
  errorIconSrc: errorIcon,
  showReportBtn: false,
});

const emit = defineEmits(['report-issue-click']);

const defaultStageConfigs: StageConfig[] = [
  {
    id: 'scrape',
    title: 'Collecting Data',
    description: 'Gathering your financial data from connected accounts',
    estimatedDuration: 30,
  },
  {
    id: 'versionUpdate',
    title: 'Processing Data',
    description: 'Updating and validating your account information',
    estimatedDuration: 20,
  },
  {
    id: 'featurama',
    title: 'Analysing Data',
    description: 'Analysing spending patterns and financial behavior',
    estimatedDuration: 25,
  },
  {
    id: 'otis',
    title: 'Generating Insights',
    description: 'Creating personalised insights and recommendations',
    estimatedDuration: 25,
  },
];

const stageConfigs = computed(() => props.flowConfig?.stageConfigs || defaultStageConfigs);

const currentStageConfig = computed(() => {
  if (!props.currentStage) {
    return stageConfigs.value[0];
  }
  return stageConfigs.value.find(stage => stage.id === props.currentStage) || stageConfigs.value[0];
});

const displayTitle = computed(() => props.flowConfig?.title || currentStageConfig.value?.title);

const displayDescription = computed(() => props.flowConfig?.description || currentStageConfig.value?.description);

const overallProgress = computed(() => {
  const totalStages = stageConfigs.value.length;
  const completedCount = props.completedStages.length;
  const currentStageIndex = props.currentStage
    ? stageConfigs.value.findIndex(stage => stage.id === props.currentStage)
    : -1;

  if (props.error) {
    return Math.max((completedCount / totalStages) * 100, 10);
  }

  if (currentStageIndex >= 0) {
    return ((completedCount + 0.5) / totalStages) * 100;
  }

  return (completedCount / totalStages) * 100;
});
</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.progress-stage-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  min-height: 60vh;
  justify-content: center;
}

.progress-header {
  text-align: center;
  margin-bottom: 40px;
}

.title {
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  margin-bottom: 12px;
}

.description {
  font-size: 16px;
  font-weight: 450;
  line-height: 1.3;
  opacity: 0.8;
  margin-top: 10px;
}

.email-text {
  font-size: 16px;
  font-weight: 450;
  line-height: 16px;
  opacity: 0.8;
  margin-top: 10px;
}

.progress-container {
  width: 100%;
  max-width: 500px;
}

.stages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.stage-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  transition: all 0.3s ease;

  &.completed {
    background-color: #f0f9f4;
    border: 1px solid #10b26c;

    .stage-indicator {
      background-color: #10b26c;
      color: white;
    }

    .stage-name {
      color: #10b26c;
      font-weight: 500;
    }
  }

  &.active {
    background-color: #fff7ed;
    border: 1px solid #f59e0b;
    animation: pulse 2s infinite;

    .stage-indicator {
      background-color: #f59e0b;
      color: white;
    }

    .stage-name {
      color: #f59e0b;
      font-weight: 500;
    }

    .stage-status {
      color: #f59e0b;
      font-size: 12px;
      opacity: 0.8;
    }
  }

  &.pending {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;

    .stage-indicator {
      background-color: #e9ecef;
      color: #6c757d;
    }

    .stage-name {
      color: #6c757d;
    }
  }
}

.stage-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.stage-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stage-name {
  font-family: CircularStdBook, sans-serif;
  font-size: 14px;
  font-weight: 450;
}

.stage-status {
  font-family: CircularStdBook, sans-serif;
  font-size: 12px;
}

.progress-bar-container {
  margin-bottom: 24px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b26c 0%, #13c636 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.progress-text {
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: #FFFFFF;
  border: 1px solid #0000001A;
  border-radius: 30px;
  width: 100%;

  p {
    padding-left: 15px;
  }
}

.error-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.error-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.error-text {
  font-family: CircularStd;
  font-size: 16px;

  strong {
    font-weight: 600;
    margin-bottom: 4px;
    display: block;
    font-size: 18px;
  }

  p {
    font-size: 14px;
    margin: 0;
    opacity: 0.9;
  }
}

.status-message {
  text-align: center;
  font-size: 16px;
  opacity: 0.8;
  margin-top: 20px;

  p {
    margin: 0;
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .progress-stage-loader {
    padding: 20px;
  }

  .title {
    font-size: 24px;
    line-height: 30px;
  }

  .stage-item {
    padding: 10px 12px;
    gap: 12px;
  }

  .stage-indicator {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
}

.report-issue {
  margin-top: 5px;
  text-align: center;
  color: #1A1A1A;
  // font-family: CircularStd;
  // font-size: 16px;
}

.report-issue a {
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500 !important;
}
</style>
